import { toNull, toUUID } from "shared/lib/zod/utils"
import { ProductPositionInstallationReferenceTypename } from "shared/types/InstallationReference"
import {
  ProductPositionAddonTypename,
  ProductPositionTypename
} from "shared/types/Project"
import {
  CreateProductPositionInputSchema,
  UpdateProductPositionInputSchema
} from "shared/types/__generated__/validation"

import { RecursiveMinimalRoomWithParent } from "entities/rooms/types/Room"
import z from "zod"

export const UpdateProductPositionVariablesSchema = z.object({
  id: z.string(),
  input: UpdateProductPositionInputSchema().extend({ dimensioning: z.any() })
})

export const DeleteProductPositionVariablesSchema = z.object({
  id: z.string()
})

export const BatchCreateProductPositionsVariablesSchema = z.object({
  projectID: z.string(),
  input: z.array(
    CreateProductPositionInputSchema().extend({ dimensioning: z.any() })
  )
})

const ProductPositionAddonSchema = z.object({
  id: z.string(),
  parentProductType: z.string(),
  variantKey: z.string(),
  __typename: z.literal(ProductPositionAddonTypename)
})

const ProductPositionAddonSchemaTransform = ProductPositionAddonSchema.extend({
  id: ProductPositionAddonSchema.shape.id.transform(toUUID),
  __typename: ProductPositionAddonSchema.shape.__typename.transform(
    () => ProductPositionAddonTypename
  )
})

const ProductPositionInstallationReferenceSchema = z.object({
  id: z.string(),
  permalink: z.string(),
  position: z.number(),
  title: z.string().nullish(),
  __typename: z
    .literal(ProductPositionInstallationReferenceTypename)
    .optional()
    .transform(() => ProductPositionInstallationReferenceTypename)
})

const ProductPositionInstallationReferenceSchemaTransform =
  ProductPositionInstallationReferenceSchema.extend({
    id: ProductPositionInstallationReferenceSchema.shape.id.transform(toUUID),
    title:
      ProductPositionInstallationReferenceSchema.shape.title.transform(toNull),
    __typename: ProductPositionInstallationReferenceSchema.shape.__typename
  })

export const MAX_LENGTH_PRODUCT_POSITION_PRICE_GROUP = 30

export const ProductPositionSchema = z.object({
  id: z.string(),
  label: z.string().nullish(),
  variantKey: z.string().min(2),
  configurationKey: z.string().nullish(),
  remark: z.string().nullish(),
  quantity: z.number().min(1),
  position: z.number().min(0),
  displayPosition: z.string().nullish(),
  isAlternative: z.boolean().nullish(),
  price: z.number().nullish(),
  priceGroup: z.string().max(MAX_LENGTH_PRODUCT_POSITION_PRICE_GROUP).nullish(),
  references: z.array(ProductPositionInstallationReferenceSchema).nullish(),
  addons: z.array(ProductPositionAddonSchema).nullish(),
  dimensioning: z.any().nullish(),
  parent: z.any().nullish(),
  hasClarificationRequest: z.boolean().nullish(),
  __typename: z
    .literal(ProductPositionTypename)
    .nullish()
    .transform(() => ProductPositionTypename)
})

export const ProductPositionSchemaTransform = ProductPositionSchema.extend({
  id: ProductPositionSchema.shape.id.transform(toUUID),
  label: ProductPositionSchema.shape.label.transform(val => val ?? ""),
  variantKey: ProductPositionSchema.shape.variantKey.transform(val =>
    val.toUpperCase()
  ),
  configurationKey:
    ProductPositionSchema.shape.configurationKey.transform(toNull),
  remark: ProductPositionSchema.shape.remark.transform(toNull),
  quantity: ProductPositionSchema.shape.quantity.transform(val => val ?? 1),
  position: ProductPositionSchema.shape.position.transform(val => val ?? 0),
  price: ProductPositionSchema.shape.price.transform(toNull),
  priceGroup: ProductPositionSchema.shape.priceGroup.transform(toNull),
  displayPosition:
    ProductPositionSchema.shape.displayPosition.transform(toNull),
  isAlternative: ProductPositionSchema.shape.isAlternative.transform(
    val => !!val
  ),
  references: z
    .array(ProductPositionInstallationReferenceSchemaTransform)
    .nullish()
    .transform(refs => refs ?? []),
  addons: z
    .array(ProductPositionAddonSchemaTransform)
    .nullish()
    .transform(addons => addons ?? []),
  dimensioning: ProductPositionSchema.shape.dimensioning.transform(toNull),
  parent: ProductPositionSchema.shape.parent.transform(toNull),
  hasClarificationRequest:
    ProductPositionSchema.shape.hasClarificationRequest.transform(toNull),
  __typename: ProductPositionSchema.shape.__typename
})

const ProductPositionEditableFieldsSchema = ProductPositionSchema.omit({
  position: true,
  addons: true,
  hasClarificationRequest: true,
  __typename: true
})

export const CreateProductPositionParamsSchema =
  ProductPositionEditableFieldsSchema.required({
    id: true,
    variantKey: true,
    quantity: true
  })

export const UpdateProductPositionParamsSchema =
  ProductPositionEditableFieldsSchema.partial().required({ id: true })

export type CreateProductPositionParams = z.infer<
  typeof CreateProductPositionParamsSchema
>
export type UpdateProductPositionParams = z.infer<
  typeof UpdateProductPositionParamsSchema
>

export interface MinimalProductPosition {
  id: string
  variantKey: string
  displayPosition?: string | null | undefined
  __typename?: typeof ProductPositionTypename
}

export interface RecursiveMinimalProductPositionWithParent
  extends MinimalProductPosition {
  parent: RecursiveMinimalRoomWithParent
}
